import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AnalyticEvent, AnalyticsBloc, AnalyticsPageName } from '@kanalytics';
import { CardBloc, DetailCardData } from '@kp/shared/components/cards/card.bloc';
import { JsonObject } from '@ktypes/models';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
})
export class VideoPlayerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() altText: string;
  @Input() analyticsPageName: AnalyticsPageName;
  @Input() image: string;
  @Input() primary = true;
  @Input() title: string;
  @Input() url: string;

  @ViewChild('player') player: ElementRef;

  isPlaying = false;

  private _analyticsJson: JsonObject = {};
  private _sentEvents: JsonObject = {};
  private _shouldPlay = false;
  private _destroy$ = new Subject<void>();

  constructor(private _analyticsBloc: AnalyticsBloc, private _cardBloc: CardBloc) {}

  ngOnInit(): void {
    this._analyticsJson['title'] = this.title;
    this._analyticsJson['url'] = this.url;
    this._sentEvents[AnalyticEvent.video_start] = false;
    this._sentEvents['video_progress_25'] = false;
    this._sentEvents['video_progress_50'] = false;
    this._sentEvents['video_progress_75'] = false;
    this._sentEvents[AnalyticEvent.video_end] = false;

    this._cardBloc.detailViewClicked.pipe(takeUntil(this._destroy$)).subscribe((data: DetailCardData) => {
      if (data) {
        this._shouldPlay = data.video;
      }
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this._shouldPlay) {
        this.play();
      }
    }, 0);
  }

  ngOnDestroy() {
    if (!this._sentEvents[AnalyticEvent.video_end]) {
      this.trackVideoProgress(AnalyticEvent.video_end);
      this._sentEvents[AnalyticEvent.video_end] = true;
    }

    // This line is needed for not playing video after the component is destroyed.
    // Not destroying the player due to potential issues when clicking back to video and due to standard set at
    // https://html.spec.whatwg.org/multipage/media.html#best-practices-for-authors-using-media-elements
    this.player.nativeElement.src = '';

    this._destroy$.next();
    this._destroy$.complete();
  }

  updateTime() {
    const currentTimeInSec = this.player.nativeElement.currentTime.toFixed();
    if (currentTimeInSec > 0) {
      if (
        this.progressPercentage >= 0 &&
        this.progressPercentage < 25 &&
        !this._sentEvents[AnalyticEvent.video_start]
      ) {
        this.trackVideoProgress(AnalyticEvent.video_start);
        this._sentEvents[AnalyticEvent.video_start] = true;
      } else if (
        this.progressPercentage >= 25 &&
        this.progressPercentage < 50 &&
        !this._sentEvents['video_progress_25']
      ) {
        this.trackVideoProgress(AnalyticEvent.video_progress);
        this._sentEvents['video_progress_25'] = true;
      } else if (
        this.progressPercentage >= 50 &&
        this.progressPercentage < 75 &&
        !this._sentEvents['video_progress_50']
      ) {
        this.trackVideoProgress(AnalyticEvent.video_progress);
        this._sentEvents['video_progress_50'] = true;
      } else if (
        this.progressPercentage >= 75 &&
        this.progressPercentage < 95 &&
        !this._sentEvents['video_progress_75']
      ) {
        this.trackVideoProgress(AnalyticEvent.video_progress);
        this._sentEvents['video_progress_75'] = true;
      }
    }
  }

  play() {
    if (this.player) {
      this.isPlaying = true;
      this.player.nativeElement.play();
    }
  }

  ended() {
    if (!this._sentEvents[AnalyticEvent.video_end]) {
      this.trackVideoProgress(AnalyticEvent.video_end);
      this._sentEvents[AnalyticEvent.video_end] = true;
    }
    this.player.nativeElement.currentTime = 0;
  }

  trackVideoProgress(analyticEvent: AnalyticEvent) {
    this._analyticsJson['progress'] = this.progressPercentage;
    this._analyticsBloc.sendEvent(
      this._analyticsBloc.createEventFromInteraction({
        page: this.analyticsPageName,
        event: analyticEvent,
        meta: this._analyticsJson,
      })
    );
  }

  get progressPercentage(): number {
    return Math.round((this.player.nativeElement.currentTime.toFixed() / this.player.nativeElement.duration) * 100);
  }
}
